import i18n from 'i18next'
import { userApi } from '~/shared/api'
import { PlanAddition } from '~/shared/constants/commonConstants'
import { getCookie } from '~/shared/helpers/getCookie'
import { getUserAccountStatusFromRawStatus } from '~/shared/helpers/getUserAccountStatus'
import { getUserStatusFromRawUserStatus } from '~/shared/helpers/getUserStatusFromRawUserStatus'
import { eventLogger } from '~/shared/services/eventLogger'
import { googleAnalyticsLogger } from '~/shared/services/googleAnalytics'
import { pinterestPixelService } from '~/shared/services/pinterestPixelService'
import {
  setAnswersFromBackendAction,
  setErrorAction,
  startFetching,
  stopFetching,
} from '~/shared/store/common/actions'
import { selectAnswers, selectLanguage } from '~/shared/store/common/selects'
import { IAppState, TAppDispatchThunk } from '~/shared/store/types'
import { TAnswers } from '~/shared/types/commonTypes'
import {
  IGetUUIDResponseRaw,
  IUserAccountStatus,
  TUserConfig,
} from '~/shared/types/userTypes'

import {
  BIND_USER,
  GET_STATUS,
  GET_USER_ACCOUNT_STATUS,
  RESEND_USER_INSTRUCTIONS,
  SAVE_PLAN_ADDITIONS,
  SEND_FACEBOOK_PARAMS,
  SEND_USER_ANSWERS,
  SEND_USER_CONFIG,
  SEND_USER_EMAIL,
} from '../actionTypes'
import { selectUserStatus, selectUserUUID, selectUserUtmTags } from '../selects'
import {
  setUUIDAction,
  setUserAccountStatusAction,
  setUserStatusAction,
} from './syncActions'

export const setUpUUIDAction =
  ({
    cohort,
    uuid: uuidFromUrl,
    utmTags: utmTagsFromUrl,
    giaApiKey,
  }: {
    cohort: string
    uuid: string | null
    utmTags: Record<string, string> | null
    giaApiKey: string
  }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuidFromStore = selectUserUUID(state)
    const uuid = uuidFromUrl || uuidFromStore
    const utmTagsFromStore = selectUserUtmTags(state)
    const locale = selectLanguage(state)
    const utmTags = utmTagsFromUrl || utmTagsFromStore

    if (uuid) {
      dispatch(setUUIDAction(uuid))
      return
    }

    const response = await userApi.getUUID({
      cohort,
      appId: giaApiKey,
      locale,
      utm: utmTags,
    })

    if (response.success) {
      const responseUUID = (response.data as IGetUUIDResponseRaw).value
      dispatch(setUUIDAction(responseUUID))
    } else {
      console.error('error')
    }
  }

export const sendFacebookParamsAction =
  (): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUserUUID(state)
    const fbp = getCookie('_fbp')
    const fbc = getCookie('_fbc')

    dispatch(startFetching(SEND_FACEBOOK_PARAMS))

    const response = await userApi.saveFacebookParams({
      uuid,
      fbp,
      fbc,
    })

    if (!response.success) {
      dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
    }

    dispatch(stopFetching(SEND_FACEBOOK_PARAMS))
  }

export const sendGoogleAdsParamsAction =
  (): any =>
  async (_, getState: () => IAppState): Promise<void> => {
    const state = getState()
    const uuid = selectUserUUID(state)

    const urlParams = new URLSearchParams(window.location.search)
    const gclid = urlParams.get('gclid') || ''
    const gaid = getCookie('_ga')

    await userApi.saveGoogleAdsParams({
      uuid,
      gclid,
      gaid,
    })
  }

export const sendUserTTCLID =
  (ttclid: string): any =>
  async (_, getState: () => IAppState): Promise<void> => {
    const state = getState()
    const uuid = selectUserUUID(state)

    await userApi.saveUserTTCLID({ ttclid, uuid })
  }

export function getUserStatusAction(uuid: string): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    dispatch(startFetching(GET_STATUS))

    const response = await userApi.getUserStatus(uuid)

    if (response.success && response.data) {
      const { state } = response.data

      const userStatus = getUserStatusFromRawUserStatus(state)

      dispatch(setUserStatusAction(userStatus))
      dispatch(setAnswersFromBackendAction(userStatus.onboarding))
    }

    if (!response.success && response.status === 404) {
      const existingUserStatus = selectUserStatus(getState())

      if (!existingUserStatus) {
        const { search } = window.location
        const urlParams = new URLSearchParams(search)
        urlParams.delete('uuid')

        // use window.location.search to reload the page with updated query params
        window.location.search = `?${urlParams}`
      }
    }

    dispatch(stopFetching(GET_STATUS))
  }
}

export const sendUserAnswersAction =
  (isFinished = false): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const answers = selectAnswers(state) as TAnswers
    const uuid = selectUserUUID(state)

    dispatch(startFetching(SEND_USER_ANSWERS))

    const response = await userApi.saveUserAnswers({
      uuid,
      answers,
      isFinished,
    })

    if (!response.success) {
      dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
    }

    dispatch(stopFetching(SEND_USER_ANSWERS))
  }

export const sendUserEmailAction =
  ({
    email,
    unsuccessCallback,
  }: {
    email: string
    unsuccessCallback: () => void
  }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUserUUID(state)

    dispatch(startFetching(SEND_USER_EMAIL))

    const response = await userApi.saveUserEmail({
      uuid,
      email,
    })

    if (!response.success) {
      if (response.status === 409) {
        dispatch(setErrorAction(i18n.t('commonComponents.haveAccountError')))
      } else {
        dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
      }

      eventLogger.logEmailPageError({ error: JSON.stringify(response.data) })
      unsuccessCallback()
      dispatch(stopFetching(SEND_USER_EMAIL))
      return
    }

    window?.axon?.('track', 'sign_up', {
      user_id: uuid,
      method: 'email',
    })
    // todo: maybe will be necessary later
    // userApi.saveEmailConsent({ uuid, consented: true })
    pinterestPixelService.trackLead()
    eventLogger.logEmailPageCompleted({ email })
    window.fbq('track', 'Lead', {}, { eventID: uuid })
    googleAnalyticsLogger.logEmailFormSubmitted(email)
    dispatch(getUserStatusAction(uuid))
    dispatch(stopFetching(SEND_USER_EMAIL))
  }

export const sendUserConfigAction =
  (config: TUserConfig, unsuccessCallback?: (() => void) | null): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUserUUID(state)

    dispatch(startFetching(SEND_USER_CONFIG))

    try {
      const response = await userApi.saveUserConfig({ uuid, config })
      if (!response.success) {
        dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
        unsuccessCallback?.()
      }
    } catch (err) {
      dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
      console.error(err)
    } finally {
      dispatch(stopFetching(SEND_USER_CONFIG))
    }
  }

export const savePlanAdditionsAction =
  (planAdditions: PlanAddition[]): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUserUUID(state)

    dispatch(startFetching(SAVE_PLAN_ADDITIONS))

    const response = await userApi.savePlanAdditions({
      uuid,
      planAdditions,
    })

    if (!response.success) {
      const errorMessage =
        response?.data?.error || i18n.t('commonComponents.commonError')

      dispatch(setErrorAction(errorMessage))
      dispatch(stopFetching(SAVE_PLAN_ADDITIONS))
      return
    }

    dispatch(stopFetching(SAVE_PLAN_ADDITIONS))
  }

export const getUserAccountStatusAction = (email: string): any => {
  return async (dispatch: TAppDispatchThunk<any>) => {
    dispatch(startFetching(GET_USER_ACCOUNT_STATUS))

    const response = await userApi.getAccountStatus(email)

    if (response.success && response.data) {
      const accountStatus: IUserAccountStatus =
        getUserAccountStatusFromRawStatus(response.data.data)

      dispatch(setUserAccountStatusAction(accountStatus))
    }
    if (!response.success && response.status === 404) {
      const accountStatus: IUserAccountStatus = {
        existingUuid: '',
        hasAccount: false,
        hasAccessToApp: false,
      }

      dispatch(setUserAccountStatusAction(accountStatus))
    }

    dispatch(stopFetching(GET_USER_ACCOUNT_STATUS))
  }
}

export const bindUserAction =
  (token: string, email: string): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUserUUID(state)

    dispatch(startFetching(BIND_USER))

    const response = await userApi.bindUser({ uuid, token })

    if (response.success) {
      eventLogger.logAccountCreated(email)
      dispatch(getUserStatusAction(uuid))
    } else {
      eventLogger.logAccountCreationFailed({
        email,
        errorReason: (response.data as any)?.error ?? 'unknown',
      })
      dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
    }

    dispatch(stopFetching(BIND_USER))
  }

export const resendUserInstructionsAction = (uuid: string): any => {
  return async (dispatch: TAppDispatchThunk<any>) => {
    dispatch(startFetching(RESEND_USER_INSTRUCTIONS))

    await userApi.sendRetenoEvent({
      retenoEvent: 'ResendInstructions',
      uuid,
    })

    dispatch(stopFetching(RESEND_USER_INSTRUCTIONS))
  }
}
